import Icon from '@components/utils/Icon'
import cn from '@components/utils/cn'
import { useIsMobile } from '@components/utils/useIsMobile'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'
import { ShiftPublishState } from '@frontend/graphql/types.generated'

import { CoverageMeter } from './CoverageMeter'
import { SlotChips } from './SlotChips'
import { RemovedRow } from './RemovedRow'
import { ShiftStatusBadge } from './ShiftStatusBadge'
import { ChangeBreakdownAccordion } from './ChangeBreakdownAccordion'
import { AssignOnlyBadge } from './AssignOnlyBadge'
import { UndoRemovalButton } from './UndoRemovalButton'
import { PendingRemovalAssignees } from './PendingRemovalAssignees'
import { SelectionCheckbox } from './SelectionCheckbox'
import { useShiftsStore } from './stores/useShiftsStore'

import { ShiftActionsMenu } from '../author_shifts/ShiftActionsMenu'
import { useEventTimeZone } from '../author_shifts/ShiftAuthoringContext'
import { PublishLink } from '../publish_roster/PublishLink'
import { hasPerShiftPublish, isPendingRemoval } from '../shifts/publishState'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('roster_board')

type RosterShiftCardProps = {
  shift: ShiftWithSlots
  onChange: () => void
}

export function RosterShiftCard({ shift, onChange }: RosterShiftCardProps) {
  const timeZone = useEventTimeZone()
  const isMobile = useIsMobile()
  const pendingRemoval = isPendingRemoval(shift)
  const selected = useShiftsStore((state) => state.selectedShiftIds.has(shift.id))
  const toggleShift = useShiftsStore((state) => state.toggleShift)

  const showChangeAccordion =
    isMobile && shift.publishState === ShiftPublishState.UnpublishedChanges && Boolean(shift.changeBreakdown)

  return (
    <div
      data-testid="roster-shift-card"
      className={cn(
        'tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-gray-200 tw:p-4 tw:shadow-xs',
        pendingRemoval ? 'tw:bg-gray-50' : 'tw:bg-white',
        selected && 'tw:ring-2 tw:ring-primary-300'
      )}
    >
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:items-start tw:gap-2">
          <SelectionCheckbox
            checked={selected}
            onChange={() => toggleShift(shift.id)}
            aria-label={t('bulk.select_shift', { role: shift.roleName })}
            className="tw:mt-1 tw:shrink-0"
          />
          <div className="tw:min-w-0">
            <div
              className={cn(
                'tw:truncate tw:font-semibold tw:text-gray-900',
                pendingRemoval && 'tw:text-gray-500 tw:line-through'
              )}
            >
              {shift.roleName}
            </div>
            <div
              className={cn(
                'tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-gray-500',
                pendingRemoval && 'tw:line-through'
              )}
            >
              <Icon type="clock" className="tw:h-3 tw:w-3" aria-hidden />
              {timeFormatter.timeRange(shift.startTime, shift.endTime, timeZone)}
            </div>
          </div>
        </div>
        {pendingRemoval ? (
          <UndoRemovalButton shiftId={shift.id} onChange={onChange} />
        ) : (
          <ShiftActionsMenu shift={shift} />
        )}
      </div>

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          {!showChangeAccordion && (
            <ShiftStatusBadge
              publishState={shift.publishState}
              changeBreakdown={shift.changeBreakdown}
              roleName={shift.roleName}
            />
          )}
          {!shift.allowSelfSelect && <AssignOnlyBadge />}
        </div>
        <div className={cn(pendingRemoval && 'tw:opacity-50')}>
          <CoverageMeter filled={shift.shiftSlots.assignedCount} total={shift.shiftSlots.totalCount} />
        </div>
      </div>

      {showChangeAccordion && shift.changeBreakdown && <ChangeBreakdownAccordion breakdown={shift.changeBreakdown} />}

      {pendingRemoval ? (
        <PendingRemovalAssignees shift={shift} />
      ) : (
        <>
          <SlotChips slots={shift.shiftSlots.nodes ?? []} publishState={shift.publishState} onChange={onChange} />
          <RemovedRow shiftId={shift.id} assignees={shift.removedAssignees ?? []} onChange={onChange} />
        </>
      )}

      {!pendingRemoval && hasPerShiftPublish(shift) && (
        <div className="tw:flex tw:justify-end tw:border-t tw:border-gray-100 tw:pt-2">
          <PublishLink shift={shift} />
        </div>
      )}
    </div>
  )
}
